import type { ActivityDetailEditableField } from '@features/kt/activity-browser/constants/activity-inline-edit.constants';

import type { InlineEditSaveStatus } from '@shared/constants/inline-edit.constants';

import { ActivityVisibility } from '@api';
import type {
  ActivityContentSection,
  ActivityContextDocument,
  ActivityInstanceDetail,
  ActivityPedagogicalCustomFieldResponse,
  ActivityPedagogicalFieldResponse,
  Duration,
  InstructionStep,
  Tag,
} from '@api';
import type { ActivityFieldLayoutItem } from '@api';

/**
 * Outcome of resolving a deep-link `activity/:id/edit` id (FE-10, contract C5/C6, scenarios
 * S5/S6/S7/S25). The store decides what happened; the page performs the follow-up navigation
 * (routing is a Presentation concern here — the store never imports the route table):
 *
 * - `loaded` — the id is the caller's OWN instance; the draft is loaded, NO fork (S7/S6).
 * - `peerInstance` — the instance GET succeeded but `isOwner` is false (colleague's tenant-shared
 *   row). No draft is loaded; the page redirects to `/?activity=<id>` for the read-only modal.
 * - `forked` — the id is a library template; a private copy was created via `{resource}:copy`
 *   (C5). The page must `router.navigate(..., { replaceUrl: true })` to `newInstanceId`'s edit
 *   URL so a refresh cannot re-fork (S5); the swap re-activates the route → resolves as `loaded`.
 * - `error` — resolution failed for a TRANSIENT reason, NOT a 404: a non-404 error (5xx /
 *   network) on the own-instance or template GET, or a failed fork (`:copy`). The teacher STAYS
 *   on the page with the error state (no library redirect, no snackbar) so a 5xx does not eject
 *   the teacher from a valid activity of theirs and a transient fork failure leaves them where
 *   they can retry (S18(a)).
 * - `unresolvable` — a TRUE 404: neither an own instance nor a library template resolves
 *   (deleted / never-existed / another teacher's private instance, all 404 — S25). NO `:copy`
 *   was ever issued. The page redirects to the library and shows an error snackbar.
 * - `superseded` — a NEWER `:id` resolution started (browser back/forward, the fork URL swap)
 *   and overtook this one across an await boundary. It means "a newer resolution took over; the
 *   page must NOT navigate and must NOT set state for this one" — the winning (latest) resolution
 *   owns the draft, the loading flag, and any follow-up. It does NOT mean "no side effect
 *   happened": the stale resolution writes NO signal and performs NO navigation, and it skips the
 *   `:copy` ONLY when it was superseded BEFORE the fork was dispatched (during the instance- or
 *   template-GET). If it is superseded WHILE `copyActivity` is already in flight, the copy has
 *   already been created server-side and is not recalled from the client — the page still does not
 *   swap the URL to it, but an orphan private copy may remain. That is an accepted best-effort
 *   limit, consistent with the plan's Q6 client-side-dedup tech-debt; a compensating server-side
 *   delete is a separate product decision (out of scope).
 */
export type ActivityEditDeepLinkOutcome =
  | { outcome: 'error' }
  | { outcome: 'forked'; newInstanceId: string }
  | { outcome: 'loaded' }
  | { outcome: 'peerInstance'; activityId: string }
  | { outcome: 'superseded' }
  | { outcome: 'unresolvable' };

/**
 * Standalone, page-level editable draft (contract C8, decision Q3).
 *
 * Holds the same activity-detail shape the modal consumes. Tags live here as a
 * **flat, complete set** — not six per-dimension lists — because the C4 tag-commit
 * payload (`PUT …/tags`, full-set replace) needs the non-modelled dimensions too
 * (`kompetencia.*`, `tevekenyseg.narrativa.*`, `intezmeny.*`, …).
 */
export type ActivityEditDraft = {
  id: string;
  name: string;
  description: string;
  subTopicId?: string;
  topicName: string;
  subTopicName: string;
  duration?: Duration;
  /** Flat, complete tag set (C8) — every dimension, modelled or not. */
  tags: Tag[];
  instructions: InstructionStep[];
  /**
   * Structured content sections (`{ id, title, contentHtml }`, C1/C2) — a full, ordered list
   * replaced as a whole on save. `id` is server-generated; the draft carries it so a successful
   * save can reconcile the optimistic list to the server-returned identifiers.
   */
  sections: ActivityContentSection[];
  attachments: ActivityInstanceDetail['attachments'];
  /**
   * Persistently linked Kontextus documents (KT-200.4, contract C10) — the teacher's own,
   * `Indexed` source files, each `{ id, fileName }`. This is a DISTINCT concept from
   * {@link ActivityEditDraft.attachments} (catalog "Letölthető anyagok"): a separate field, never
   * merged with attachments. Mutated via immediate link/unlink calls, NOT the `commitField` path.
   */
  contextDocuments: ActivityContextDocument[];
  isFavorite: boolean;
  isAiGenerated: boolean;
  visibility: ActivityVisibility;
  /** Per-activity field order and visibility on the edit card (KT-903). */
  fieldLayout: ActivityFieldLayoutItem[];
  /** Pedagogical fields from `GET …/pedagogical-fields` (KT-660). Not part of the detail GET. */
  pedagogicalFields: ActivityPedagogicalFieldResponse[];
  /** Activity-owned custom pedagogical fields (KT-1081). */
  customPedagogicalFields: ActivityPedagogicalCustomFieldResponse[];
  /** Whether the loaded instance belongs to the signed-in teacher. */
  isOwner: boolean;
  /**
   * The current sticker's opaque media path, or `null`. A read-only seed from the loaded detail: no field commit
   * merges it into an existing draft, so an autosave response can never overwrite the sticker the card shows.
   */
  stickerImageUrl: string | null;
  /**
   * The current cover and its generation record, read-only seeds from the loaded detail: no field commit merges them
   * into an existing draft, so an autosave response can never overwrite what the header shows. The cover's live
   * state is the cover generation queue's.
   */
  cover: ActivityInstanceDetail['cover'];
  coverGeneration: ActivityInstanceDetail['coverGeneration'];
};

/**
 * Page-level field commit (contract C1): a field identifier + its typed value — nothing
 * more. The generation marker that guards against late / out-of-order save responses
 * (C1, scenario S28) is tracked store-internally (`#saveGeneration`), never carried on
 * the commit. The field set is the KT-155 set — the `kompetencia` dimension is
 * intentionally excluded (Q19).
 */
export type ActivityEditFieldCommit =
  | { field: 'description'; value: string }
  | { field: 'duration'; value: Duration }
  | {
      field: 'grade' | 'locations' | 'subject' | 'subtype' | 'tools' | 'type' | 'workform';
      value: ActivityEditTagCommitValue;
    }
  | { field: 'instructions'; value: InstructionStep[] }
  | { field: 'name'; value: string }
  | { field: 'sections'; value: ActivityContentSection[] }
  | { field: 'subtopic' | 'topic'; value: ActivityEditSubTopicCommitValue }
  | { field: 'visibility'; value: ActivityVisibility };

/**
 * Page-level field save status. Reuses the KT-155 {@link InlineEditSaveStatus}
 * (`idle` | `saving` | `error`) and adds the terminal `saved` state the dedicated
 * edit page surfaces after a successful autosave.
 */
export type ActivityEditFieldSaveStatus = 'saved' | InlineEditSaveStatus;

export type ActivityEditFieldSaveStatuses = Record<ActivityDetailEditableField, ActivityEditFieldSaveStatus>;

/**
 * What the edit store's `flushPending()` found among the saves that settled while it ran:
 *
 * - `ok` — none failed (a superseded save does not count; its newer save does).
 * - `failed` — a field save failed.
 * - `failedAlreadyNotified` — only a rejected section image source failed; the page has shown its own snackbar.
 */
export type ActivityEditFlushResult = 'failed' | 'failedAlreadyNotified' | 'ok';

/** Typed value for the subtopic/topic commit. */
export type ActivityEditSubTopicCommitValue = {
  subTopicId: string;
  subTopicName?: string;
  topicName?: string;
};

/** Typed value for a chip-dimension commit (already resolved to the full tag set). */
export type ActivityEditTagCommitValue = {
  tagIds: string[];
  tags: Tag[];
};

/**
 * Narrows a dropdown value to a known {@link ActivityVisibility}.
 *
 * @param value The raw selected value.
 * @returns Whether the value is `Private` or `Tenant`.
 */
export function isActivityVisibility(value: string): value is ActivityVisibility {
  return (Object.values(ActivityVisibility) as string[]).includes(value);
}

/**
 * Maps a loaded {@link ActivityInstanceDetail} into the editable page-level draft.
 *
 * @param detail The loaded activity instance detail response.
 * @returns The editable page-level draft.
 */
export function mapActivityInstanceDetailToEditDraft(detail: ActivityInstanceDetail): ActivityEditDraft {
  return {
    id: detail.id,
    name: detail.name,
    description: detail.description,
    subTopicId: detail.subTopicId,
    topicName: detail.topicName,
    subTopicName: detail.subTopicName,
    duration: detail.duration ? { ...detail.duration } : undefined,
    tags: [...detail.tags],
    instructions: [...detail.instructions],
    // Deep-copy each section object so the draft never aliases the loaded detail's items — a
    // later in-place edit of a draft section must not mutate the response snapshot.
    sections: detail.sections.map((section) => ({ ...section })),
    attachments: [...detail.attachments],
    // Kept on its OWN field, never merged with `attachments`. Deep-copy each document object so
    // the draft never aliases the loaded detail's items (contract C10).
    contextDocuments: detail.contextDocuments.map((contextDocument) => ({ ...contextDocument })),
    isFavorite: detail.isFavorite,
    isAiGenerated: detail.isAiGenerated,
    visibility: detail.visibility,
    fieldLayout: (detail.fieldLayout ?? []).map((item) => ({ ...item })),
    stickerImageUrl: detail.stickerImageUrl,
    cover: detail.cover,
    coverGeneration: detail.coverGeneration,
    pedagogicalFields: [],
    customPedagogicalFields: [],
    isOwner: detail.isOwner,
  };
}
